// src/router/index.ts (or .js)
import Vue from 'vue';
import Router from 'vue-router';
import { ENV } from '@/env';
Vue.use(Router);
// Lazy components
const Home = () => import('@/views/Home.vue');
const UserProfile = () => import('@/views/user/UserProfile.vue');
const Category = () => import('@/views/catalog/Category.vue');
const CarouselListing = () => import('@/views/catalog/CarouselListing.vue');
const BrandListing = () => import('@/views/catalog/BrandListing.vue');
const Product = () => import('@/views/catalog/Product.vue');
const Account = () => import('@/views/account/Account.vue');
const AccountTrackOrder = () => import('@/views/account/TrackOrder.vue');
const Cart = () => import('@/views/checkout/Cart.vue');
const Checkout = () => import('@/views/checkout/Checkout.vue');
const CheckoutConfirmation = () => import('@/views/checkout/CheckoutConfirmation.vue');
const PaymentFailure = () => import('@/views/checkout/PaymentFailure.vue');
const CheckoutPayment = () => import('@/views/checkout/CheckoutPayment.vue');
const Search = () => import('@/views/Search.vue');
const Faqs = () => import('@/views/Faqs.vue');
const Documentation = () => import('@/views/Documentation.vue');
const PasswordReset = () => import('@/views/auth/PasswordReset.vue');
const Page = () => import('@/views/page/Page.vue');
const NotFound = () => import('@/views/NotFound.vue');
const AuthCallback = () => import('@/views/auth/AuthCallback.vue');
const Maintenance = () => import('@/pages/Maintenance.vue');
const Dashboard = () => import('@/components/my-account/dashboard/dashboard.component.vue');
const Vouchers = () => import('@/components/my-account/vouchers/vouchers.component.vue');
const AccountDetails = () => import('@/components/my-account/details/details.component.vue');
const Orders = () => import('@/components/my-account/orders/orders.component.vue');
const OrderDetails = () => import('@/components/my-account/order-details/order-details.component.vue');
const Wishlist = () => import('@/components/my-account/wishlist/wishlist.component.vue');
const TrackOrder = () => import('@/components/my-account/track-order/track-order.component.vue');
const InsuranceOrders = () => import('@/components/my-account/orders/insurance-orders.component.vue');
const FxOrders = () => import('@/components/my-account/orders/fx-orders.component.vue');
const Login = () => import('@/modules/auth/containers/Login.vue');
const Callback = () => import('@/modules/auth/containers/Callback.vue');
const Logout = () => import('@/modules/auth/containers/Logout.vue');
// Helpers
function isNovaLike(slug) {
    if (!slug || Array.isArray(slug))
        return false;
    const s = String(slug).trim().toLowerCase();
    return s === 'nova' || s.startsWith('nova-');
}
async function resolveLegacySlugToProductSlug(legacySlug) {
    try {
        const res = await fetch(`${ENV.API_BASE_URL}/pages/resolve-legacy-slug/${encodeURIComponent(legacySlug)}`, {
            credentials: 'include'
        });
        if (!res.ok)
            return null;
        const data = await res.json();
        return (data && data.productSlug) || null;
    }
    catch {
        return null;
    }
}
const routes = [
    { path: "/ciam/login", name: 'ciam.login', component: Login, meta: { documentNavigation: true } },
    // old callback path -> new callback path
    { path: "/callback", redirect: "/ciam/callback", meta: { documentNavigation: true } },
    // canonical callback route
    { path: "/ciam/callback", name: "ciam.callback", component: Callback, meta: { documentNavigation: true } },
    { path: "/ciam/logout", name: 'ciam.logout', component: Logout, meta: { documentNavigation: true } },
    // Legacy /ticket URLs -> homepage
    {
        path: '/ticket',
        redirect: { name: 'changi_recommends.index' },
    },
    {
        path: '/ticket/:any(.*)',
        redirect: { name: 'changi_recommends.index' },
    },
    {
        path: '/maintenance',
        name: 'maintenance',
        component: Maintenance
    },
    {
        path: '/request-token-from-myaccount',
        name: 'changi_recommends.request_token_from_myaccount',
        meta: { documentNavigation: true },
        beforeEnter: (_to, _from, next) => {
            window.location.href = `${ENV.BACKEND_URL}/request-token-from-myaccount`;
            next(false);
        }
    },
    { path: '/auth/callback', name: 'l5-swagger.get-it.oauth2_callback', component: AuthCallback, meta: { documentNavigation: true } },
    { path: '/', name: 'changi_recommends.index', component: Home },
    { path: '/user/:any(.*)?', name: 'changi_recommends.user.profile', component: UserProfile },
    { path: '/category/:categoryPath(.*)', name: 'changi_recommends.category', component: Category, props: true },
    { path: '/products/:carouselSlug/:categoryPath(.*)?', name: 'changi_recommends.carousel_listing', component: CarouselListing },
    { path: '/brand/:brandSlug/:categoryPath(.*)?', name: 'changi_recommends.brand_listing', component: BrandListing },
    { path: '/product/:productSlug/:productVariantSlug?', name: 'changi_recommends.product', component: Product, props: true },
    { path: '/product-vue/:productSlug', redirect: to => ({ name: 'changi_recommends.product', params: { productSlug: to.params.productSlug } }) },
    // { path: '/account/track-order/:any(.*)?', name: 'changi_recommends.user.account.track-order', component: AccountTrackOrder },
    {
        path: '/account/track-order/:uuid',
        component: AccountTrackOrder,
        name: 'account.trackOrderDetails',
        props: true,
    },
    {
        path: '/account/track-order',
        component: AccountTrackOrder,
        name: 'account.trackOrder',
        props: true,
    },
    {
        path: '/account',
        component: Account,
        children: [
            // /account -> /account/dashboard
            { path: '', redirect: 'dashboard' },
            {
                path: 'dashboard',
                name: 'account.dashboard',
                component: Dashboard,
                meta: { requiresAuth: true },
            },
            {
                path: 'vouchers',
                name: 'account.vouchers',
                component: Vouchers,
                meta: { requiresAuth: true },
            },
            {
                path: 'details',
                name: 'account.details',
                component: AccountDetails,
                meta: { requiresAuth: true },
            },
            {
                path: 'orders',
                name: 'account.orders',
                component: Orders,
                meta: { requiresAuth: true },
            },
            {
                path: 'insurance-orders',
                name: 'account.insuranceOrders',
                component: InsuranceOrders,
                meta: { requiresAuth: true },
            },
            {
                path: 'fx-orders',
                name: 'account.fxOrders',
                component: FxOrders,
                meta: { requiresAuth: true },
            },
            {
                path: 'order-details/:orderUuid',
                name: 'account.orderDetails',
                component: OrderDetails,
                props: true,
                meta: { requiresAuth: true },
            },
            {
                path: 'wishlist',
                name: 'account.wishlist',
                component: Wishlist,
                meta: { requiresAuth: true },
            },
        ],
    },
    { path: '/cart', component: Cart },
    { path: '/checkout', name: 'changi_recommends.checkout', component: Checkout },
    { path: '/checkout-confirmation', name: 'changi_recommends.checkout.confirmation', component: CheckoutConfirmation },
    { path: '/payment-failure', name: 'changi_recommends.checkout.payment_failure', component: PaymentFailure },
    { path: '/checkout-payment', name: 'changi_recommends.checkout.payment', component: CheckoutPayment },
    { path: '/search/:query', component: Search, props: true },
    { path: '/faqs', component: Faqs },
    { path: '/get-it-documentation', component: Documentation },
    { path: '/password/reset', name: 'changi_recommends.password_reset_form', component: PasswordReset },
    // {
    //   path: '/product-page/:legacySlug',
    //   name: 'changi_recommends.redirect_slug',
    //   // async guard is fine in Vue 2
    //   beforeEnter: async (to, _from, next) => {
    //     const legacy = String(to.params.legacySlug || '')
    //     const newSlug = await resolveLegacySlugToProductSlug(legacy)
    //     if (newSlug) return next({ name: 'changi_recommends.product', params: { productSlug: newSlug } })
    //     return next({ name: 'changi_recommends.simple_pages', params: { slug: legacy } })
    //   }
    // },
    // {
    //   path: '/:slug/:param?',
    //   name: 'changi_recommends.simple_pages',
    //   component: Page,
    //   props: true,
    //   beforeEnter: (to, _from, next) => {
    //     if (isNovaLike(to.params.slug)) {
    //       window.location.href = `${ENV.BACKEND_URL}/${to.fullPath.replace(/^\//, '')}`
    //       return next(false)
    //     }
    //     return next()
    //   }
    // },
    // {
    //   path: '/feed-proxy',
    //   name: 'changi_recommends.feed-proxy',
    //   beforeEnter: (_to, _from, next) => {
    //     window.location.href = 'https://discoverist.changirecommends.com/feed'
    //     next(false)
    //   }
    // },
    // Vue 2 catch-all
    // { path: '*', name: 'not-found', component: NotFound }
    {
        path: '*',
        redirect: { name: 'changi_recommends.index' }
    }
];
import CheckoutPayNow from '../pages/CheckoutPayNow.vue';
import CheckoutPaymentFailure from '@/pages/CheckoutPaymentFailure.vue';
export var CheckoutRoute;
(function (CheckoutRoute) {
    CheckoutRoute["Checkout"] = "Checkout";
    CheckoutRoute["CheckoutPayNow"] = "CheckoutPayNow";
    CheckoutRoute["CheckoutAtomePayment"] = "CheckoutAtomePayment";
    CheckoutRoute["CheckoutConfirmation"] = "CheckoutConfirmation";
    CheckoutRoute["CheckoutPaymentFailure"] = "CheckoutPaymentFailure";
})(CheckoutRoute || (CheckoutRoute = {}));
export const checkoutRoutes = [
    {
        path: '/checkout-payment',
        name: CheckoutRoute.CheckoutPayNow,
        component: CheckoutPayNow,
    },
    {
        path: '/payment-failure',
        name: CheckoutRoute.CheckoutPaymentFailure,
        component: CheckoutPaymentFailure,
    },
];
// Create router with global navigation guard for maintenance mode
const router = new Router({
    mode: "history",
    base: ENV.BASE_URL,
    routes,
    scrollBehavior(to, _from, savedPosition) {
        if (savedPosition)
            return savedPosition;
        if (to.hash)
            return { selector: to.hash };
        return { x: 0, y: 0 };
    },
});
// Simple maintenance check function
function isMaintenanceActive() {
    // Check if maintenance data exists in localStorage or sessionStorage
    // This will be set by the App.vue when maintenance data is fetched
    try {
        const maintenanceData = sessionStorage.getItem('maintenance_data');
        if (maintenanceData) {
            const maintenance = JSON.parse(maintenanceData);
            if (maintenance.enabled) {
                const now = new Date();
                const start = maintenance.start ? new Date(maintenance.start) : null;
                const end = maintenance.end ? new Date(maintenance.end) : null;
                if (start && end && now >= start && now <= end) {
                    return true;
                }
            }
        }
    }
    catch (e) {
        console.error('Error checking maintenance status:', e);
    }
    return false;
}
// ----------------------
// Cross-subdomain routing
// ----------------------
const LOGIN_HOST = process.env.VUE_APP_LOGIN_HOST || "login.cr.local";
const LOGIN_ORIGIN = process.env.VUE_APP_LOGIN_ORIGIN || "https://login.cr.local:8080";
const DEFAULT_MAIN_ORIGIN = process.env.VUE_APP_DEFAULT_MAIN_ORIGIN ||
    "https://attractions.cr.local:8081";
const CIAM_ALLOWED_ON_LOGIN = new Set([
    "/ciam/login",
    "/callback",
    "/ciam/callback",
    "/ciam/logout",
]);
function isLoginHost() {
    return typeof window !== "undefined" && window.location.hostname === LOGIN_HOST;
}
function getMainOrigin() {
    // If you store the “main origin” somewhere during login flow, use it.
    // Otherwise fall back to a default main origin.
    try {
        return sessionStorage.getItem("ciam.mainOrigin") || DEFAULT_MAIN_ORIGIN;
    }
    catch {
        return DEFAULT_MAIN_ORIGIN;
    }
}
router.beforeEach((to, _from, next) => {
    const onLogin = isLoginHost();
    // 1) If we're NOT on login host, but user hits CIAM routes -> move them to login host
    if (!onLogin && CIAM_ALLOWED_ON_LOGIN.has(to.path)) {
        window.location.replace(`${LOGIN_ORIGIN}${to.fullPath}`);
        return; // stop navigation
    }
    // 2) If we're ON login host, only allow CIAM routes; everything else -> go back to main host
    if (onLogin && !CIAM_ALLOWED_ON_LOGIN.has(to.path)) {
        const mainOrigin = getMainOrigin();
        window.location.replace(`${mainOrigin}${to.fullPath}`);
        return; // stop navigation
    }
    next();
});
export default router;
